import { useEffect, useRef, useState, useCallback } from 'react'
import { Application, Graphics, Container, Text, TextStyle } from 'pixi.js'
import { io } from 'socket.io-client'
import { fmtCoins } from '../../lib/cards'
import { Sounds, Music } from '../../lib/sounds'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'

interface Props {
  token:       string
  balance:     number | null
  displayName: string
  operatorId:  string
  playerId:    string
}

type Phase      = 'idle' | 'active' | 'hopping' | 'cashed_out' | 'cooked'
type Difficulty = 'easy' | 'medium' | 'hard' | 'hardcore'

const SURVIVAL    = { easy: 0.91, medium: 0.82, hard: 0.70, hardcore: 0.55 }
const DIFF_LABEL  = { easy: 'Easy', medium: 'Medium', hard: 'Hard', hardcore: 'Hardcore' }
const DIFF_CHANCE = { easy: '9%', medium: '18%', hard: '30%', hardcore: '45%' }
const DIFF_CSS    = { easy: '#22c55e', medium: '#3b82f6', hard: '#f59e0b', hardcore: '#ef4444' }
const CHIP_VALS   = [1, 5, 10, 50, 100, 500]
const CHIP_CSS    = ['#64748b','#22c55e','#3b82f6','#8b5cf6','#f59e0b','#ef4444']

function getMult(d: Difficulty, n: number) {
  return Math.floor(0.97 / Math.pow(SURVIVAL[d], n) * 100) / 100
}

export function ChickenRoadPixi({ token, balance, displayName, operatorId, playerId }: Props) {
  const mountRef  = useRef<HTMLDivElement>(null)
  const appRef    = useRef<Application | null>(null)
  const socketRef = useRef<any>(null)
  const stateRef  = useRef({
    phase: 'idle' as Phase,
    step: 0, mults: [] as number[],
    stake: 50, diff: 'easy' as Difficulty,
    SW: 0, SY: 0, VIS: 6, anchorX: 0,
    camX: 0, camFromX: 0, camToX: 0,
    worldFromX: 0, worldToX: 0,
    chickenWX: 0, chickenY: 0,
    hopActive: false, hopT: 0,
    failT: 0,
  })

  const [phase,       setPhase]       = useState<Phase>('idle')
  const [diff,        setDiff]        = useState<Difficulty>('easy')
  const [stake,       setStake]       = useState(50)
  const [step,        setStep]        = useState(0)
  const [localBal,    setLocalBal]    = useState<number | null>(balance)
  const [toast,       setToast]       = useState<string | null>(null)
  const [payout,      setPayout]      = useState(0)
  const [cashoutMult, setCashoutMult] = useState(0)
  const [showCooked,  setShowCooked]  = useState(false)
  const [muted,       setMuted]       = useState(false)
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  const needsRedraw = useRef(false)

  useEffect(() => { setLocalBal(balance) }, [balance])

  const showToast = (msg: string, dur = 3000) => {
    setToast(msg); setTimeout(() => setToast(null), dur)
  }

  // world X of tile n (n=0 is the START coin)
  const worldX = (n: number) => {
    const st = stateRef.current
    return n === 0 ? st.SW / 2 + 20 : st.SW * 1.5 + 20 + (n - 1) * st.SW + st.SW / 2
  }
  // camera target so chicken sits at the anchor (left third of screen)
  const camTarget = (n: number) => Math.max(0, worldX(n) - stateRef.current.anchorX)

  // ── Socket ──────────────────────────────────────────────
  useEffect(() => {
    const GS = import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3002'
    const s = io(GS, {
      transports:           ['polling'],
      auth:                 { token },
      reconnectionAttempts: 20,
      reconnectionDelay:    500,
      timeout:              20000,
    })
    socketRef.current = s

    s.on('chicken:start_result', (data: any) => {
      if (data.success) {
        const st = stateRef.current
        st.mults = data.multipliers; st.step = 0
        st.phase = 'active'; st.stake = stake
        st.camX = 0; st.camFromX = 0; st.camToX = 0
        st.chickenWX = worldX(0); st.chickenY = st.SY - 36
        setStep(0); setPhase('active')
        setLocalBal(prev => prev !== null ? prev - stake : prev)
        needsRedraw.current = true
      } else {
        showToast(data.error || 'Failed to start')
        stateRef.current.phase = 'idle'; setPhase('idle')
      }
    })

    s.on('chicken:hop_result', (data: any) => {
      if (!data.success) { showToast(data.error || 'Hop failed'); return }
      if (data.safe) {
        const st = stateRef.current
        const newStep = data.step
        st.worldFromX = worldX(st.step)
        st.worldToX   = worldX(newStep)
        st.camFromX   = st.camX
        st.camToX     = camTarget(newStep)
        st.hopT = 0; st.hopActive = true
        st.step = newStep; st.phase = 'hopping'
        setStep(newStep); setPhase('hopping')
        Sounds.hop()
        needsRedraw.current = true
      } else {
        stateRef.current.phase = 'cooked'; stateRef.current.failT = 0
        setPhase('cooked'); setShowCooked(false)
        Sounds.cooked(); Music.stop()
        showToast('🔥 Cooked! Better luck next time!')
        needsRedraw.current = true
        setTimeout(() => setShowCooked(true), 2500)
      }
    })

    s.on('chicken:cashout_result', (data: any) => {
      if (data.success) {
        setPayout(data.payout); setCashoutMult(data.multiplier)
        stateRef.current.phase = 'cashed_out'; setPhase('cashed_out')
        setLocalBal(prev => prev !== null ? prev + data.payout : prev)
        Sounds.cashout(); Music.stop()
        showToast(`Cashed out at ${data.multiplier}x!`)
        needsRedraw.current = true
      }
    })

    return () => { s.disconnect() }
  }, [token])

  // ── PixiJS Setup ────────────────────────────────────────
  useEffect(() => {
    if (!mountRef.current) return
    const container = mountRef.current
    const W = container.clientWidth || 600
    const H = container.clientHeight ? Math.min(container.clientHeight, 380) : 320
    const mobile = W < 500

    const app = new Application({
      width: W, height: H, backgroundColor: 0x151926,
      antialias: true, resolution: window.devicePixelRatio || 1, autoDensity: true,
    })
    appRef.current = app
    container.appendChild(app.view as HTMLCanvasElement)
    app.ticker.maxFPS = 30

    const VIS = mobile ? 4 : 6
    const SW  = (W - 40) / (VIS + 0.5)
    const SY  = H * 0.52
    const st0 = stateRef.current
    st0.SW = SW; st0.SY = SY; st0.VIS = VIS
    st0.anchorX = 20 + SW * 1.5 + SW / 2   // chicken rests near 2nd visible tile
    st0.chickenWX = worldX(0); st0.chickenY = SY - 36
    st0.camX = 0

    // Layers: bg (fixed) → track (scrolls) → chicken → ui
    const bgG      = new Graphics(); app.stage.addChild(bgG)
    const trackC   = new Container(); app.stage.addChild(trackC)
    const trackG   = new Graphics(); trackC.addChild(trackG)
    const trackTxt = new Container(); trackC.addChild(trackTxt)
    const chickenG = new Graphics(); app.stage.addChild(chickenG)
    const uiG      = new Graphics(); app.stage.addChild(uiG)
    const uiTxt    = new Container(); app.stage.addChild(uiTxt)

    function mkText(txt: string, style: object, x: number, y: number, c: Container) {
      const t = new Text(txt, new TextStyle({ fontFamily:'system-ui', ...style } as any))
      t.anchor.set(0.5); t.x = x; t.y = y; c.addChild(t)
    }

    function drawCoin(g: Graphics, x: number, y: number, r = 26) {
      g.beginFill(0xC77700).drawCircle(x,y,r+2).endFill()
      g.beginFill(0xFFD700).drawCircle(x,y,r).endFill()
      g.lineStyle(3,0xFFD700).drawCircle(x,y,r).lineStyle(0)
      g.beginFill(0xD84315).drawEllipse(x,y-3,9,12).endFill()
      g.beginFill(0xFF7043).drawEllipse(x,y-11,6,4).endFill()
      g.beginFill(0xF5F5F5).drawRoundedRect(x-9,y+5,18,4,2).endFill()
      g.beginFill(0xffffff,0.22).drawEllipse(x-8,y-9,9,11).endFill()
    }

    function drawOven(g: Graphics, x: number, y: number, burn: boolean, t: number, r: number) {
      if (burn) g.beginFill(0xff6f00, 0.12+Math.sin(t*3)*0.06).drawCircle(x,y,r+7).endFill()
      g.beginFill(burn?0x424242:0x2e3035).lineStyle(2,0x161616).drawCircle(x,y,r+4).endFill()
      g.lineStyle(0)
      for (let i=0;i<8;i++){const a=i*Math.PI/4;g.beginFill(0x5a5d63).drawCircle(x+Math.cos(a)*(r+2),y+Math.sin(a)*(r+2),2.2).endFill()}
      g.beginFill(0x1a1a1a).drawCircle(x,y,r-3).endFill()
      if (burn){
        for(let i=0;i<3;i++){
          const ox=Math.sin(t*4+i*1.3)*2,al=0.7+Math.sin(t*5+i)*0.2
          g.beginFill(0xffeb3b,al*0.9).drawEllipse(x+ox,y+3,4+i*2,r-6).endFill()
          g.beginFill(0xff9800,al*0.8).drawEllipse(x+ox,y+5,3+i,r-9).endFill()
          g.beginFill(0xff5722,al*0.6).drawEllipse(x+ox,y+7,2,r-13).endFill()
        }
      } else {
        g.beginFill(0x0a0a0a).drawCircle(x,y,r-5).endFill()
      }
      const gr=r-5; g.lineStyle(1.5,0x000000,0.85)
      for(let i=-2;i<=2;i++){const oy=i*gr/2.5,hw=Math.sqrt(Math.max(0,gr*gr-oy*oy));g.moveTo(x-hw*0.88,y+oy).lineTo(x+hw*0.88,y+oy)}
      for(let i=-1;i<=1;i++){const ox=i*gr/1.8,hh=Math.sqrt(Math.max(0,gr*gr-ox*ox));g.moveTo(x+ox,y-hh*0.88).lineTo(x+ox,y+hh*0.88)}
      g.lineStyle(0)
    }

    function drawChicken(g: Graphics, x: number, y: number, sx=1, sy=1, dead=false, deadT=0) {
      if (dead) {
        const alpha=Math.max(0,1-Math.max(0,deadT-0.6)*2.5)
        const spin=deadT*Math.PI*5
        const shake=deadT<0.3?Math.sin(deadT*80)*8:0
        const bx=x+shake
        if(deadT>0.05&&deadT<0.7){
          for(let i=0;i<8;i++){
            const angle=(i/8)*Math.PI*2+deadT*6,dist=deadT*55,sa=Math.max(0,1-deadT*1.8)
            g.beginFill(0xfbbf24,sa).drawCircle(bx+Math.cos(angle)*dist,y+Math.sin(angle)*dist,3).endFill()
            g.beginFill(0xef4444,sa*0.7).drawCircle(bx+Math.cos(angle+0.5)*dist*0.6,y+Math.sin(angle+0.5)*dist*0.6-10,2).endFill()
          }
        }
        if(deadT>0.08&&deadT<0.75){
          const fa=Math.min(1,(deadT-0.08)*4)*Math.max(0,1-(deadT-0.5)*4)
          for(let i=0;i<5;i++){
            const fx=bx+(i-2)*10
            g.beginFill(0xffeb3b,fa*0.9).drawEllipse(fx,y-5,6,14).endFill()
            g.beginFill(0xff9800,fa*0.8).drawEllipse(fx,y-1,4,10).endFill()
            g.beginFill(0xff5722,fa*0.6).drawEllipse(fx,y+3,3,6).endFill()
          }
        }
        if(deadT>0.3){
          const sa=Math.min(1,(deadT-0.3)*2)*Math.max(0,1-deadT*1.1)
          for(let i=0;i<4;i++){
            const angle=(i/4)*Math.PI*2+deadT*2,dist=(deadT-0.3)*45
            g.beginFill(0x888888,sa*0.25).drawCircle(bx+Math.cos(angle)*dist,y+Math.sin(angle)*dist-deadT*25,9+i*3).endFill()
          }
        }
        const cosS=Math.cos(spin),sinS=Math.sin(spin)
        const rx=(dx:number,dy:number)=>bx+cosS*dx-sinS*dy
        const ry=(dx:number,dy:number)=>y+sinS*dx+cosS*dy
        const bc=deadT>0.35?0xc4622a:0xffffff
        g.beginFill(bc,alpha).drawEllipse(bx,y+8,20,22).endFill()
        g.beginFill(bc,alpha).drawCircle(rx(4,-16),ry(4,-16),13).endFill()
        g.beginFill(0xe74c3c,alpha);[-2,3,8].forEach(ox=>g.drawEllipse(rx(ox,-27),ry(ox,-27),3,5));g.endFill()
        g.lineStyle(1,0xe67e22,alpha).beginFill(0xf1c40f,alpha)
        g.moveTo(rx(13,-16),ry(13,-16)).lineTo(rx(21,-14),ry(21,-14)).lineTo(rx(13,-12),ry(13,-12)).closePath().endFill()
        g.lineStyle(2.5,0x1a1a1a,alpha)
        ;[[8,-20],[15,-20]].forEach(([ex,ey])=>{
          g.moveTo(rx(ex-3,ey-3),ry(ex-3,ey-3)).lineTo(rx(ex+3,ey+3),ry(ex+3,ey+3))
          g.moveTo(rx(ex+3,ey-3),ry(ex+3,ey-3)).lineTo(rx(ex-3,ey+3),ry(ex-3,ey+3))
        })
        g.lineStyle(0); return
      }
      g.beginFill(0,0.12).drawEllipse(x,y+45*sy,16*sx,4).endFill()
      g.lineStyle(1,0xe5e5e5).beginFill(0xffffff)
      g.drawEllipse(x-19*sx,y+5*sy,7*sx,10*sy).endFill()
      g.drawEllipse(x-21*sx,y-1*sy,6*sx,8*sy).endFill()
      g.lineStyle(1.5,0xe5e5e5).beginFill(0xffffff).drawEllipse(x,y+8*sy,20*sx,24*sy).endFill()
      g.lineStyle(1,0xe5e5e5).beginFill(0xf8f8f8).drawEllipse(x-10*sx,y+7*sy,8*sx,13*sy).endFill()
      g.lineStyle(0).beginFill(0xffffff).drawEllipse(x,y-5*sy,12*sx,12*sy).endFill()
      g.lineStyle(1.5,0xe5e5e5).beginFill(0xffffff).drawCircle(x+3*sx,y-18*sy,14*Math.max(sx,0.7)).endFill()
      g.lineStyle(0).beginFill(0xe74c3c)
      ;[-2,3,9].forEach((ox,i)=>g.drawEllipse(x+ox*sx,y-(29+i*1.5)*sy,3*sx,6*sy));g.endFill()
      g.beginFill(0xff5252).drawEllipse(x+4*sx,y-3*sy,3*sx,5*sy).endFill()
      g.lineStyle(1,0xe67e22).beginFill(0xf1c40f)
      g.moveTo(x+13*sx,y-18*sy).lineTo(x+22*sx,y-16*sy).lineTo(x+13*sx,y-14*sy).closePath().endFill()
      g.lineStyle(0).beginFill(0x1a1a1a).drawCircle(x+9*sx,y-20*sy,2.8).endFill()
      g.beginFill(0xffffff).drawCircle(x+10*sx,y-21*sy,0.9).endFill()
      g.beginFill(0xff9999,0.5).drawEllipse(x+13*sx,y-14*sy,4*sx,3*sy).endFill()
      g.lineStyle(3,0xf39c12,1,0.5)
      g.moveTo(x-4*sx,y+33*sy).lineTo(x-6*sx,y+47*sy)
      g.moveTo(x+7*sx,y+33*sy).lineTo(x+9*sx,y+47*sy)
      g.lineStyle(1.8,0xe67e22,1,0.5)
      ;[[x-6*sx,y+47*sy],[x+9*sx,y+47*sy]].forEach(([bx2,by2])=>{
        ;[[-8,6],[0,7],[8,6]].forEach(([tx,ty])=>g.moveTo(bx2,by2).lineTo(bx2+tx*sx,by2+ty*sy))
      })
      g.lineStyle(0)
    }

    // Fixed background
    function drawBg() {
      bgG.clear()
      bgG.beginFill(0x151926).drawRect(0,0,W,H).endFill()
      bgG.lineStyle(0.5,0xffffff,0.025)
      for(let x=0;x<W;x+=70)bgG.moveTo(x,0).lineTo(x,H)
      for(let y2=0;y2<H;y2+=55)bgG.moveTo(0,y2).lineTo(W,y2)
      bgG.lineStyle(0)
    }

    // Track layer — world coordinates, scrolled via trackC.x = -camX
    function redrawTrack(t = performance.now()/1000) {
      const st = stateRef.current
      trackG.clear(); trackTxt.removeChildren()
      const ovenR = Math.min(28, SW * 0.36)
      drawCoin(trackG, worldX(0), SY, Math.min(26, SW*0.34))
      mkText('START',{fontSize:9,fill:0x334155},worldX(0),SY+36,trackTxt)
      mkText('→',{fontSize:16,fill:0x334155},worldX(0)+SW*0.62,SY+4,trackTxt)
      // draw a window: a bit behind, a screen ahead
      const first = Math.max(1, st.step - 2)
      const last  = Math.min(st.mults.length, st.step + VIS + 2)
      for (let sn = first; sn <= last; sn++) {
        const mult = st.mults[sn-1]
        if (mult === undefined) continue
        const sx = worldX(sn)
        const isA = sn === st.step, isP = sn < st.step, isN = sn === st.step + 1
        drawOven(trackG, sx, SY, !isP, t, ovenR)
        if (isA) { trackG.lineStyle(3,0x22c55e,0.7).drawCircle(sx,SY,ovenR+6).lineStyle(0) }
        else if (isN && (st.phase==='active'||st.phase==='hopping')) { trackG.lineStyle(2,0x6366f1,0.5).drawCircle(sx,SY,ovenR+6).lineStyle(0) }
        const bFill=isA?0x22c55e:isP?0x22c55e:0x6366f1
        const bAlpha=isA?0.4:isP?0.2:0.2
        const bStr=isA||isP?0x22c55e:0x6366f1
        const bSA=isA?0.9:isP?0.5:isN?0.7:0.3
        const badgeR = Math.min(26, SW*0.34)
        trackG.lineStyle(2,bStr,bSA).beginFill(bFill,bAlpha).drawCircle(sx,SY-50,badgeR).endFill().lineStyle(0)
        const mc=isA?0x86efac:isP?0x4ade80:0xa5b4fc
        mkText(mult.toFixed(2)+'x',{fontSize:mobile?10:11,fontWeight:'bold',fill:mc,fontFamily:'monospace'},sx,SY-46,trackTxt)
        mkText('#'+sn,{fontSize:9,fill:0x334155},sx,SY+39,trackTxt)
      }
    }

    // Dynamic layer — chicken + current-mult UI
    function drawDynamic(t: number, st: typeof stateRef.current) {
      chickenG.clear(); uiG.clear(); uiTxt.removeChildren()
      const screenX = (wx: number) => wx - st.camX
      if (st.phase==='hopping' && st.hopActive) {
        const p = st.hopT
        const wx = st.worldFromX + (st.worldToX - st.worldFromX) * p
        const cy = (SY-36) - Math.sin(p*Math.PI) * 70
        const ssx = 1 - Math.sin(p*Math.PI)*0.2, ssy = 1 + Math.sin(p*Math.PI)*0.35
        st.chickenWX = wx; st.chickenY = cy
        drawChicken(chickenG, screenX(wx), cy, ssx, ssy)
        if (p<0.15){chickenG.beginFill(0xffffff,(0.15-p)*5).drawEllipse(screenX(st.worldFromX),SY-16,20*(1-p*6),5).endFill()}
        if (p>0.85){chickenG.beginFill(0xffffff,(p-0.85)*4).drawEllipse(screenX(st.worldToX),SY-16,20*((p-0.85)*6),5).endFill()}
      } else if (st.phase==='cooked') {
        st.failT = Math.min(st.failT+0.012,1)
        drawChicken(chickenG, screenX(st.chickenWX), st.chickenY, 1, 1, true, st.failT)
      } else {
        drawChicken(chickenG, screenX(st.chickenWX), SY-36)
      }
      if ((st.phase==='active'||st.phase==='hopping') && st.step>0) {
        const m = getMult(st.diff, st.step)
        const bw = mobile ? 100 : 116, bx = W - bw - 8
        uiG.beginFill(0x0f172a,0.9).lineStyle(1,0x22c55e,0.3).drawRoundedRect(bx,8,bw,58,8).endFill().lineStyle(0)
        mkText('CURRENT',{fontSize:9,fill:0x64748b,letterSpacing:1},bx+bw/2,20,uiTxt)
        mkText(m.toFixed(2)+'x',{fontSize:mobile?17:20,fontWeight:'bold',fill:0x22c55e,fontFamily:'monospace'},bx+bw/2,40,uiTxt)
        mkText('₹'+Math.floor(st.stake*m),{fontSize:11,fill:0x86efac},bx+bw/2,56,uiTxt)
      }
    }

    // Ticker
    app.ticker.add((delta) => {
      const t = performance.now()/1000, st = stateRef.current
      if (st.hopActive) {
        st.hopT += delta * 0.055
        // camera eases along with the hop
        const e = st.hopT >= 1 ? 1 : (1 - Math.pow(1 - Math.min(st.hopT,1), 3))
        st.camX = st.camFromX + (st.camToX - st.camFromX) * e
        if (st.hopT >= 1) {
          st.hopT = 1; st.hopActive = false
          st.camX = st.camToX
          st.chickenWX = st.worldToX; st.chickenY = SY-36
          st.phase = 'active'; setPhase('active')
          Sounds.land(); Sounds.tick()
          needsRedraw.current = true
        }
      }
      trackC.x = -st.camX
      if (needsRedraw.current) { redrawTrack(t); needsRedraw.current = false }
      const needsAnim = st.hopActive || st.phase==='idle' || st.phase==='active' || st.phase==='hopping' || st.phase==='cooked'
      if (needsAnim) drawDynamic(t, st)
    })

    drawBg()
    redrawTrack()
    return () => {
      app.ticker.stop()
      app.destroy(true,{children:true,texture:true})
      appRef.current = null
    }
  }, [])

  const startGame = useCallback(() => {
    if(!socketRef.current||stake===0)return
    Sounds.go();Music.start()
    stateRef.current.diff=diff;stateRef.current.stake=stake;stateRef.current.phase='hopping'
    setPhase('hopping')
    socketRef.current.emit('chicken:start',{player_id:playerId,operator_id:operatorId,difficulty:diff,stake})
  },[stake,diff,playerId,operatorId])

  const hop = useCallback(() => {
    if(!socketRef.current||phase!=='active')return
    socketRef.current.emit('chicken:hop',{player_id:playerId,operator_id:operatorId})
  },[phase,playerId,operatorId])

  const cashout = useCallback(() => {
    if(!socketRef.current||phase!=='active'||step===0)return
    socketRef.current.emit('chicken:cashout',{player_id:playerId,operator_id:operatorId})
  },[phase,step,playerId,operatorId])

  const reset = () => {
    setShowCooked(false);Music.stop()
    const st=stateRef.current
    st.phase='idle';st.step=0;st.mults=[]
    st.hopActive=false;st.failT=0
    st.camX=0;st.camFromX=0;st.camToX=0
    st.chickenWX=st.SW/2+20;st.chickenY=st.SY-36
    setPhase('idle');setStep(0);setPayout(0)
    needsRedraw.current=true
  }

  const toggleMute=()=>{const n=!muted;setMuted(n);Music.setVolume(n?0:0.18)}
  const currentMult=step>0?getMult(diff,step):1

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',width:'100%',maxWidth:'100vw',overflowX:'hidden',background:'#151926',color:'#e2e8f0',fontFamily:"'DM Sans',system-ui,sans-serif"}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:isMobile?'8px 10px':'8px 14px',background:'#0f172a',borderBottom:'1px solid rgba(255,255,255,0.08)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
          <span style={{fontSize:18}}>🐔</span>
          <strong style={{fontSize:isMobile?12:14,letterSpacing:.5,whiteSpace:'nowrap'}}>CHICKEN ROAD</strong>
          <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:4,whiteSpace:'nowrap',
            background:phase==='active'||phase==='hopping'?'rgba(34,197,94,.2)':phase==='cooked'?'rgba(239,68,68,.2)':phase==='cashed_out'?'rgba(201,168,76,.2)':'rgba(34,197,94,.2)',
            color:phase==='active'||phase==='hopping'?'#22c55e':phase==='cooked'?'#ef4444':phase==='cashed_out'?'#c9a84c':'#22c55e'}}>
            {phase==='idle'?'Ready':phase==='active'?'In Game':phase==='hopping'?'Hopping...':phase==='cooked'?'Cooked! 🔥':'Won! 🏆'}
          </span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:isMobile?6:10}}>
          {!isMobile&&<span style={{fontSize:11,color:'#475569'}}>Step: {step}</span>}
          <SoundToggleBtn />
          <div style={{background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.25)',borderRadius:8,padding:'4px 10px',fontWeight:700,color:'#c9a84c',fontSize:isMobile?11:13,whiteSpace:'nowrap'}}>
            BAL {localBal!==null?`₹${fmtCoins(localBal)}`:'—'}
          </div>
        </div>
      </div>

      {toast&&(
        <div style={{position:'absolute',top:54,left:'50%',transform:'translateX(-50%)',background:'rgba(34,197,94,.9)',borderRadius:8,padding:'6px 16px',fontSize:12,fontWeight:600,color:'white',zIndex:100,whiteSpace:'nowrap',boxShadow:'0 4px 16px rgba(0,0,0,.4)',pointerEvents:'none'}}>
          {toast}
        </div>
      )}

      <div ref={mountRef} style={{flex:1,position:'relative',overflow:'hidden',minHeight:isMobile?260:320,maxHeight:isMobile?380:undefined}}>
        {phase==='cashed_out'&&(
          <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,.8)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:10,zIndex:20,backdropFilter:'blur(4px)'}}>
            <div style={{fontSize:isMobile?34:44,fontWeight:900,background:'linear-gradient(90deg,#c9a84c,#f0e68c,#c9a84c)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',letterSpacing:2}}>YOU WIN!</div>
            <div style={{fontSize:isMobile?22:28,fontWeight:800,color:'#c9a84c'}}>+{fmtCoins(payout)} coins</div>
            <div style={{fontSize:13,color:'#64748b'}}>at {cashoutMult.toFixed(2)}x multiplier</div>
            <button onClick={reset} style={{marginTop:8,padding:'10px 28px',background:'#16a34a',border:'none',borderRadius:8,color:'white',fontSize:14,fontWeight:700,cursor:'pointer'}}>Play Again</button>
          </div>
        )}
        {showCooked&&(
          <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,.75)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:8,zIndex:20,backdropFilter:'blur(4px)'}}>
            <div style={{fontSize:isMobile?34:44,fontWeight:900,color:'#ef4444',textShadow:'0 0 30px rgba(239,68,68,.8)',letterSpacing:2}}>COOKED! 🔥</div>
            <div style={{fontSize:14,color:'#f87171'}}>Better luck next time!</div>
            <button onClick={reset} style={{marginTop:8,padding:'10px 28px',background:'#dc2626',border:'none',borderRadius:8,color:'white',fontSize:14,fontWeight:700,cursor:'pointer'}}>Try Again</button>
          </div>
        )}
      </div>

      <div style={{flexShrink:0,background:'#0f172a',borderTop:'2px solid #1e293b',marginTop:'auto'}}>
        <div style={{display:'flex',alignItems:'center',gap:8,padding:isMobile?'8px 10px':'8px 14px',borderBottom:'1px solid #1e293b',flexWrap:'wrap'}}>
          {!isMobile&&<span style={{fontSize:11,color:'#475569',whiteSpace:'nowrap'}}>Difficulty</span>}
          {(['easy','medium','hard','hardcore'] as Difficulty[]).map(d=>(
            <button key={d} onClick={()=>{if(phase==='idle'){setDiff(d);stateRef.current.diff=d}}} disabled={phase!=='idle'}
              style={{padding:isMobile?'7px 10px':'5px 12px',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:phase==='idle'?'pointer':'default',background:`${DIFF_CSS[d]}22`,color:DIFF_CSS[d],outline:diff===d?`2px solid ${DIFF_CSS[d]}`:'none',opacity:phase!=='idle'?.5:1,transition:'all .15s',flex:isMobile?1:undefined}}>
              {DIFF_LABEL[d]}
            </button>
          ))}
          <span style={{fontSize:11,color:'#475569',marginLeft:isMobile?0:'auto',whiteSpace:'nowrap',width:isMobile?'100%':undefined,textAlign:isMobile?'right':undefined}}>
            Collision: <strong style={{color:DIFF_CSS[diff]}}>{DIFF_CHANCE[diff]}</strong>
          </span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8,padding:isMobile?'10px 10px':'10px 14px'}}>
          <div style={{display:'flex',gap:4,overflowX:'auto',WebkitOverflowScrolling:'touch',minWidth:0,maxWidth:isMobile?'40%':undefined}}>
            {CHIP_VALS.map((v,i)=>(
              <button key={v} onClick={()=>{if(phase==='idle'){setStake(v);stateRef.current.stake=v}}} disabled={phase!=='idle'}
                style={{width:32,height:32,borderRadius:'50%',background:`radial-gradient(circle at 35% 35%,${CHIP_CSS[i]}cc,${CHIP_CSS[i]}88)`,border:stake===v?'2px solid white':'2px solid rgba(255,255,255,.2)',color:'white',fontSize:10,fontWeight:700,cursor:phase==='idle'?'pointer':'default',opacity:phase==='idle'?1:.4,transition:'all .15s',flexShrink:0}}>
                {v>=1000?`${v/1000}k`:v}
              </button>
            ))}
          </div>
          <div style={{padding:'6px 10px',background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.3)',borderRadius:6,color:'#c9a84c',fontWeight:700,fontSize:13,minWidth:48,textAlign:'center',flexShrink:0}}>
            ₹{stake}
          </div>
          {phase==='idle'&&(
            <button onClick={startGame} style={{flex:1,minWidth:80,padding:11,background:'linear-gradient(135deg,#16a34a,#15803d)',border:'none',borderRadius:8,color:'white',fontSize:15,fontWeight:900,cursor:'pointer',boxShadow:'0 4px 16px rgba(34,197,94,.3)',letterSpacing:.5}}>
              GO 🐔
            </button>
          )}
          {(phase==='active'||phase==='hopping')&&(<>
            <button onClick={cashout} disabled={step===0||phase==='hopping'}
              style={{flex:1,minWidth:0,padding:'11px 6px',borderRadius:8,fontSize:isMobile?11:13,fontWeight:700,background:step>0?'rgba(201,168,76,.15)':'rgba(255,255,255,.04)',border:`1px solid ${step>0?'rgba(201,168,76,.5)':'rgba(255,255,255,.1)'}`,color:step>0?'#c9a84c':'#334155',cursor:step>0&&phase==='active'?'pointer':'default',opacity:phase==='hopping'?.5:1,whiteSpace:'nowrap'}}>
              CASH OUT{step>0?` ₹${Math.floor(stake*currentMult)}`:''}
            </button>
            <button onClick={hop} disabled={phase==='hopping'}
              style={{flex:1,minWidth:0,padding:'11px 6px',border:'none',borderRadius:8,color:'white',fontSize:isMobile?13:15,fontWeight:900,background:phase==='hopping'?'#1e293b':'linear-gradient(135deg,#f59e0b,#d97706)',cursor:phase==='hopping'?'default':'pointer',opacity:phase==='hopping'?.6:1,boxShadow:phase!=='hopping'?'0 4px 16px rgba(245,158,11,.3)':'none',whiteSpace:'nowrap'}}>
              {phase==='hopping'?'Hopping...':'HOP →'}
            </button>
          </>)}
          {(phase==='cashed_out'||phase==='cooked')&&(
            <button onClick={reset} style={{flex:1,minWidth:80,padding:11,background:'#1e293b',border:'1px solid #334155',borderRadius:8,color:'#94a3b8',fontSize:13,fontWeight:700,cursor:'pointer'}}>
              New Game
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
